// Resolve a YouTube video id from an item's url, falling back to its external_id when that looks
// like an 11-char video id. Returns null when no id can be determined (e.g. non-YouTube content).
export function extractYouTubeId(
  url: string | null | undefined,
  externalId: string
): string | null {
  if (url) {
    try {
      const u = new URL(url);
      if (u.hostname.includes('youtu.be')) {
        return u.pathname.slice(1).split('?')[0] ?? null;
      }

      const v = u.searchParams.get('v');
      if (v) {
        return v;
      }
    } catch {
      /* Not a valid URL — fall through to external_id */
    }
  }

  if (/^[\w-]{11}$/.test(externalId)) {
    return externalId;
  }

  return null;
}

const VIDEO_ID_PATTERN = /(?:v=|\/(?:embed|shorts)\/|youtu\.be\/|\/([\w-]{11}))([\w-]{11})/;
const BARE_VIDEO_ID_PATTERN = /^[\w-]{11}$/;
const CHANNEL_ID_PATTERN = /^UC[\w-]{22}$/;
const HANDLE_PATTERN = /^@[\p{Letter}\p{Mark}\p{Number}\-._]+$/u;

function parseHandle(candidate: string): string | null {
  try {
    const decoded = decodeURIComponent(candidate);
    return HANDLE_PATTERN.test(decoded) ? decoded : null;
  } catch {
    return null;
  }
}

export type YouTubeInputKind =
  | {kind: 'video'; id: string}
  | {kind: 'channel'; id: string}
  | {kind: 'handle'; handle: string}
  | {kind: 'unknown'};

/** Classifies a pasted YouTube video URL/ID, channel URL/ID, or `@handle` so the Submission page can auto-detect which flow to run (Scrapy video ingestion vs. a channel status lookup). */
export function classifyYouTubeInput(raw: string): YouTubeInputKind {
  const input = raw.trim();
  if (!input) {
    return {kind: 'unknown'};
  }

  if (CHANNEL_ID_PATTERN.test(input)) {
    return {kind: 'channel', id: input};
  }

  const channelUrlMatch = /\/channel\/(UC[\w-]{22})/.exec(input);
  if (channelUrlMatch) {
    return {kind: 'channel', id: channelUrlMatch[1] ?? input};
  }

  const handleUrlMatch = /youtube\.com\/(@[^\s#/?]+)/.exec(input);
  if (handleUrlMatch) {
    const handle = parseHandle(handleUrlMatch[1] ?? input);
    return handle ? {kind: 'handle', handle} : {kind: 'unknown'};
  }

  if (input.startsWith('@')) {
    const handle = parseHandle(input);
    return handle ? {kind: 'handle', handle} : {kind: 'unknown'};
  }

  const videoMatch = VIDEO_ID_PATTERN.exec(input);
  if (videoMatch) {
    return {kind: 'video', id: videoMatch[1] ?? videoMatch[2] ?? input};
  }

  if (BARE_VIDEO_ID_PATTERN.test(input)) {
    return {kind: 'video', id: input};
  }

  return {kind: 'unknown'};
}

export type ClassifiedLine = {raw: string; parsed: YouTubeInputKind};

/** Splits a multiline textarea of channel URLs/IDs/@handles into de-duplicated, classified lines. */
export function classifyChannelLines(text: string): ClassifiedLine[] {
  const seen = new Set<string>();
  const results: ClassifiedLine[] = [];

  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) {
      continue;
    }

    const parsed = classifyYouTubeInput(trimmed);
    const key = parsed.kind === 'unknown' ? trimmed : JSON.stringify(parsed);
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    results.push({raw: trimmed, parsed});
  }

  return results;
}
